import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

export default function ReferralPage({ user }) {
 const [referralData, setReferralData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [copied, setCopied] = useState(false);

 useEffect(() => {
 const fetchReferrals = async () => {
 try {
 const token = localStorage.getItem('token');
 const { data } = await axios.get('/api/referrals', {
 headers: { Authorization: `Bearer ${token}` }
 });
 setReferralData(data);
 } catch (err) {
 console.error('Failed to load referrals:', err);
 } finally {
 setLoading(false);
 }
 };
 fetchReferrals();
 }, []);

 const copyToClipboard = () => {
 if (referralData?.referral_link) {
 navigator.clipboard.writeText(referralData.referral_link);
 setCopied(true);
 setTimeout(() => setCopied(false), 3000);
 }
 };

 const shareReferral = async () => {
 if (navigator.share && referralData?.referral_link) {
 try {
 await navigator.share({
 title: 'Join ShiftMatch',
 text: 'Find flexible work on ShiftMatch! Sign up with my referral link:',
 url: referralData.referral_link,
 });
 } catch (err) {
 // User cancelled
 }
 } else {
 copyToClipboard();
 }
 };

 const totalEarnings = (referralData?.total_credited || 0) * (referralData?.reward_per_referral || 20);

 if (loading) {
 return (
 <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
 <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-neutral-50">
 <SEOHelmet title="Refer & Earn" description="Refer friends to ShiftMatch and earn $20 per referral when they complete their first shift." />
 
 <div className="max-w-3xl mx-auto px-4 py-12">
 {/* Header */}
 <div className="text-center mb-10">
 <div className="text-5xl mb-4"></div>
 <h1 className="text-3xl font-bold text-neutral-900 mb-2">Refer & Earn</h1>
 <p className="text-neutral-500">Share your referral link and earn <strong className="text-brand-600">$20</strong> for every friend who signs up and completes a shift!</p>
 </div>

 {/* Earnings Card */}
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 mb-8">
 <div className="grid grid-cols-3 gap-6 text-center">
 <div>
 <p className="text-3xl font-bold text-brand-600">${totalEarnings}</p>
 <p className="text-sm text-neutral-500 mt-1">Total Earned</p>
 </div>
 <div>
 <p className="text-3xl font-bold text-neutral-900">{referralData?.total_credited || 0}</p>
 <p className="text-sm text-neutral-500 mt-1">Referrals Credited</p>
 </div>
 <div>
 <p className="text-3xl font-bold text-neutral-900">${referralData?.reward_per_referral || 20}</p>
 <p className="text-sm text-neutral-500 mt-1">Per Referral</p>
 </div>
 </div>
 </div>

 {/* Referral Link */}
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 mb-8">
 <h2 className="text-lg font-semibold text-neutral-900 mb-4">Your Referral Link</h2>
 <div className="flex gap-2">
 <input
 type="text"
 readOnly
 value={referralData?.referral_link || ''}
 className="flex-1 border border-neutral-200 rounded-xl px-4 py-3 bg-neutral-50 text-neutral-700 text-sm font-mono"
 />
 <button
 onClick={copyToClipboard}
 className="px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl transition whitespace-nowrap"
 >
 {copied ? '✓ Copied!' : 'Copy'}
 </button>
 <button
 onClick={shareReferral}
 className="px-6 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-semibold rounded-xl transition"
 >
 Share
 </button>
 </div>
 <p className="text-xs text-neutral-400 mt-3">Your code: <span className="font-mono font-bold text-brand-600">{referralData?.referral_code}</span></p>
 </div>

 {/* How It Works */}
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 mb-8">
 <h2 className="text-lg font-semibold text-neutral-900 mb-4">How It Works</h2>
 <div className="space-y-4">
 <div className="flex items-start gap-4">
 <div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center font-bold shrink-0">1</div>
 <div>
 <p className="font-medium text-neutral-900">Share your link</p>
 <p className="text-sm text-neutral-500">Send your referral link to friends via text, email, or social media</p>
 </div>
 </div>
 <div className="flex items-start gap-4">
 <div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center font-bold shrink-0">2</div>
 <div>
 <p className="font-medium text-neutral-900">They sign up</p>
 <p className="text-sm text-neutral-500">Your friend creates a free ShiftMatch account using your link</p>
 </div>
 </div>
 <div className="flex items-start gap-4">
 <div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center font-bold shrink-0">3</div>
 <div>
 <p className="font-medium text-neutral-900">You earn $20</p>
 <p className="text-sm text-neutral-500">Once they complete their first shift, you get $20 credited to your account</p>
 </div>
 </div>
 </div>
 </div>

 {/* Referral List */}
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-8">
 <h2 className="text-lg font-semibold text-neutral-900 mb-4">Your Referrals</h2>
 {referralData?.referrals?.length > 0 ? (
 <div className="space-y-3">
 {referralData.referrals.map((ref) => (
 <div key={ref.id} className="flex items-center justify-between py-3 border-b border-neutral-50 last:border-0">
 <div>
 <p className="font-medium text-neutral-900">{ref.referred_name || ref.referred_email || 'Anonymous'}</p>
 <p className="text-xs text-neutral-400">{new Date(ref.created_at).toLocaleDateString()}</p>
 </div>
 <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
 ref.status === 'credited' ? 'bg-brand-100 text-brand-700' :
 ref.status === 'joined' ? 'bg-blue-100 text-blue-700' :
 'bg-neutral-100 text-neutral-500'
 }`}>
 {ref.status === 'credited' ? `$${ref.reward_amount}` : ref.status === 'joined' ? 'Joined' : 'Pending'}
 </span>
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-8 text-neutral-400">
 <p className="text-3xl mb-2"></p>
 <p>No referrals yet. Share your link to start earning!</p>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}
